/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import {
  BruttoNettoInputs,
  BruttoNettoResult,
  EinkommensteuerInputs,
  EinkommensteuerResult,
  SparrechnerInputs,
  SparrechnerResult,
  KreditrechnerInputs,
  KreditrechnerResult,
  MinijobInputs,
  MinijobResult,
  SeveranceInputs,
  SeveranceResult,
  AbgeltungssteuerInputs,
  AbgeltungssteuerResult,
  AussergewoehnlicheBelastungenInputs,
  AussergewoehnlicheBelastungenResult,
  StufenDetail,
  BUNDESLAENDER,
  BruttoRechnerInputs,
  BruttoRechnerResult,
  ErbschaftsteuerInputs,
  ErbschaftsteuerResult,
  Verwandtschaftsgrad
} from '../types';

/**
 * Official German Income Tax (Einkommensteuertarif) for 2025/2026/2027.
 * Formulas and coefficients are based on German Einkommensteuergesetz (EStG) § 32a.
 * Returns the annual income tax amount.
 */
export function calculateGermanIncomeTax(taxableIncome: number, year: number = 2027): number {
  // Rounded down to the next full euro according to German tax practice
  const y = Math.floor(taxableIncome);
  if (y <= 0) return 0;

  if (year >= 2027) {
    // 2027 Projected Tariff under § 32a EStG (Kalte Progression inflation adjustment)
    const gfb = 12552;
    if (y <= gfb) return 0;

    const zone1End = 17500;
    if (y <= zone1End) {
      const y_param = (y - gfb) / 10000;
      const tax = (895.0 * y_param + 1400) * y_param;
      return Math.floor(tax);
    }

    const zone2End = 70500;
    if (y <= zone2End) {
      const z_param = (y - 17500) / 10000;
      const tax = (178.0 * z_param + 2397) * z_param + 895.0;
      return Math.floor(tax);
    }

    const zone3End = 285000;
    if (y <= zone3End) {
      const tax = 0.42 * y - 10920.0;
      return Math.floor(tax);
    }

    // Zone 4: Reichensteuer 45%
    const tax = 0.45 * y - 19470.0;
    return Math.floor(tax);
  } else if (year === 2026) {
    // 2026 Tariff under § 32a EStG
    const gfb = 12348;
    if (y <= gfb) return 0;

    const zone1End = 17300;
    if (y <= zone1End) {
      const y_param = (y - gfb) / 10000;
      const tax = (902.38 * y_param + 1400) * y_param;
      return Math.floor(tax);
    }

    const zone2End = 69800;
    if (y <= zone2End) {
      const z_param = (y - 17300) / 10000;
      const tax = (179.35 * z_param + 2397) * z_param + 902.34;
      return Math.floor(tax);
    }

    const zone3End = 282000;
    if (y <= zone3End) {
      const tax = 0.42 * y - 10814.24;
      return Math.floor(tax);
    }

    // Zone 4: Reichensteuer
    const tax = 0.45 * y - 19274.24;
    return Math.floor(tax);
  } else {
    // 2025 Tariff under § 32a EStG
    const gfb = 12096;
    if (y <= gfb) return 0;

    const zone1End = 17006;
    if (y <= zone1End) {
      const y_param = (y - gfb) / 10000;
      const tax = (912.17 * y_param + 1400) * y_param;
      return Math.floor(tax);
    }

    const zone2End = 68449;
    if (y <= zone2End) {
      const z_param = (y - 17006) / 10000;
      const tax = (181.38 * z_param + 2397) * z_param + 907.32;
      return Math.floor(tax);
    }

    const zone3End = 277825;
    if (y <= zone3End) {
      const tax = 0.42 * y - 10602.13;
      return Math.floor(tax);
    }

    // Zone 4: Reichensteuer
    const tax = 0.45 * y - 18936.88;
    return Math.floor(tax);
  }
}

/**
 * Calculat Brutto-Netto wage components for Germany.
 */
export function calculateBruttoNetto(inputs: BruttoNettoInputs): BruttoNettoResult {
  const bruttogehalt = inputs.bruttogehalt;
  const jahresBrutto = bruttogehalt * 12;

  // 1. Social Security Contributions (Sozialabgaben) - 2025/2026 limits
  // Beitragsbemessungsgrenzen (monthly max)
  const bbgKV = 5175; // Krankenversicherung & Pflegeversicherung
  const bbgRV = 7550; // Rentenversicherung & Arbeitslosenversicherung

  // Cap the salaries for social security base calculations
  const baseKV = Math.min(bruttogehalt, bbgKV);
  const baseRV = Math.min(bruttogehalt, bbgRV);

  // Pension Insurance (Rentenversicherung: 18.6% - shared 50/50)
  const rvSatz = 0.093; 
  const rvBeitrag = baseRV * rvSatz;

  // Unemployment Insurance (Arbeitslosenversicherung: 2.6% - shared 50/50)
  const avSatz = 0.013;
  const avBeitrag = baseRV * avSatz;

  // Health Insurance (Krankenversicherung: 14.6% shared + custom Zusatzbeitrag shared)
  const kvZusatzSatzAb = inputs.zusatzbeitrag / 100; // e.g. 1.7% -> 0.017
  const kvSatzMitarbeiter = 0.073 + (kvZusatzSatzAb / 2);
  const kvBeitrag = baseKV * kvSatzMitarbeiter;

  // Care Insurance (Pflegeversicherung: 3.4% base. Childless aged 23+ pays additional 0.6%)
  // Employer pays 1.7%. Employee pays 1.7% base + 0.6% if childless.
  // In Saxony, employer pays less and employee pays more (special rule simplified here)
  const isChildless = !inputs.kinder;
  const pvSatzMitarbeiter = isChildless ? 0.023 : 0.017;
  const pvBeitrag = baseKV * pvSatzMitarbeiter;

  const sozialabgaben = rvBeitrag + avBeitrag + kvBeitrag + pvBeitrag;

  // 2. Taxes (Lohnsteuer)
  // Calculate taxable income estimate (adjust based on tax class)
  // German tax classes give high-level standard deductions:
  // Class 1 (Single): Standard
  // Class 2 (Single Parent): Extra allowance ~4,260
  // Class 3 (Married High Earn): Double standard allowance (lower tax)
  // Class 4 (Married Equal): Standard
  // Class 5 (Married Low Earn): Very low allowance
  // Class 6 (Multi Job): No allowance
  
  let abzugFreibetrag = 0;
  let werbungskostenPauschale = 1230; // standard EUR 1,230 Werbungslose
  let vorsorgePauschale = jahresBrutto * 0.2; // simplified pension contribution writeoff
  if (vorsorgePauschale > 20000) vorsorgePauschale = 20000;

  if (inputs.steuerklasse === 1) {
    abzugFreibetrag = 0;
  } else if (inputs.steuerklasse === 2) {
    abzugFreibetrag = 4260; 
  } else if (inputs.steuerklasse === 3) {
    abzugFreibetrag = 12096; // married booster
  } else if (inputs.steuerklasse === 4) {
    abzugFreibetrag = 0;
  } else if (inputs.steuerklasse === 5) {
    abzugFreibetrag = -8000; // shift to spouse Class 3
  } else if (inputs.steuerklasse === 6) {
    abzugFreibetrag = -12096; // penalized
  }

  let taxableIncomeJahres = jahresBrutto - werbungskostenPauschale - vorsorgePauschale - abzugFreibetrag;
  if (inputs.kinder && inputs.kinderZahl > 0) {
    const kinderFreibetrag = inputs.kinderZahl * (inputs.jahr === 2026 ? 9600 : 9312);
    taxableIncomeJahres -= kinderFreibetrag * 0.3; // effective tax relief factor
  }
  
  if (taxableIncomeJahres < 0) taxableIncomeJahres = 0;

  // Annual tax
  const annualLohnsteuer = calculateGermanIncomeTax(taxableIncomeJahres, inputs.jahr);
  const lohnsteuer = annualLohnsteuer / 12;

  // Solidarity surcharge (Soli) with Milderungszone (gradual phase-in)
  let annualSoli = 0;
  let singleFreigrenze = 18130;
  let marriedFreigrenze = 36260;
  if (inputs.jahr === 2025) {
    singleFreigrenze = 19950;
    marriedFreigrenze = 39900;
  } else if (inputs.jahr === 2026) {
    singleFreigrenze = 20340;
    marriedFreigrenze = 40680;
  }
  const soliFreigrenze = inputs.steuerklasse === 3 ? marriedFreigrenze : singleFreigrenze;
  if (annualLohnsteuer > soliFreigrenze) {
    // Soli calculation with Milderungszone according to \u00A7 4 SolzG:
    // It is the minimum of 5.5% of the tax or 11.9% of the excess amount above the freigrenze.
    annualSoli = Math.min(annualLohnsteuer * 0.055, (annualLohnsteuer - soliFreigrenze) * 0.119);
  }
  const soli = Math.max(0, annualSoli / 12);

  // Church Tax (Kirchensteuer): 8% or 9% of lohnsteuer
  let kirchensteuer = 0;
  if (inputs.kirchensteuer) {
    const selectedState = BUNDESLAENDER.find(b => b.id === inputs.bundesland);
    const rate = selectedState ? selectedState.kircheRate : 0.09;
    kirchensteuer = lohnsteuer * rate;
  }

  const steuern = lohnsteuer + soli + kirchensteuer;
  const netto = bruttogehalt - sozialabgaben - steuern;

  // Employer Contributions (AG Anteile)
  const rvEmployer = baseRV * 0.093;
  const avEmployer = baseRV * 0.013;
  const kvEmployer = baseKV * (0.073 + (kvZusatzSatzAb / 2));
  const pvEmployer = baseKV * 0.017; // employer always pays 1.7%
  const ArbeitgeberBeitrag = rvEmployer + avEmployer + kvEmployer + pvEmployer;
  const gesamtKostenArbeitgeber = bruttogehalt + ArbeitgeberBeitrag;

  return {
    brutto: bruttogehalt,
    rentenversicherung: Math.round(rvBeitrag * 100) / 100,
    arbeitslosenversicherung: Math.round(avBeitrag * 100) / 100,
    krankenversicherung: Math.round(kvBeitrag * 100) / 100,
    pflegeversicherung: Math.round(pvBeitrag * 100) / 100,
    sozialabgaben: Math.round(sozialabgaben * 100) / 100,
    lohnsteuer: Math.round(lohnsteuer * 100) / 100,
    soli: Math.round(soli * 100) / 100,
    kirchensteuer: Math.round(kirchensteuer * 100) / 100,
    steuern: Math.round(steuern * 100) / 100,
    netto: Math.round(netto * 100) / 100,
    arbeitgeberAnteil: Math.round(ArbeitgeberBeitrag * 100) / 100,
    gesamtKostenArbeitgeber: Math.round(gesamtKostenArbeitgeber * 100) / 100,
  };
}

/**
 * Calculate complete Einkommensteuer (Income Tax) and comparison data.
 */
export function calculateEinkommensteuer(inputs: EinkommensteuerInputs): EinkommensteuerResult {
  const isJoint = inputs.veranlagung === 'gemeinsam';
  const taxableIncome = inputs.einkommen;
  const year = inputs.jahr;

  // Split-Tariff: for joint filing, income is split in half, calculated, and doubled
  let tax = 0;
  if (isJoint) {
    const halfIncome = taxableIncome / 2;
    tax = calculateGermanIncomeTax(halfIncome, year) * 2;
  } else {
    tax = calculateGermanIncomeTax(taxableIncome, year);
  }

  // Soli with Milderungszone (gradual phase-in) according to \u00A7 4 SolzG
  let soli = 0;
  let singleFreigrenze = 18130;
  let marriedFreigrenze = 36260;
  if (year === 2025) {
    singleFreigrenze = 19950;
    marriedFreigrenze = 39900;
  } else if (year === 2026) {
    singleFreigrenze = 20340;
    marriedFreigrenze = 40680;
  }
  const soliFreigrenze = isJoint ? marriedFreigrenze : singleFreigrenze;
  if (tax > soliFreigrenze) {
    soli = Math.min(tax * 0.055, (tax - soliFreigrenze) * 0.119);
  }

  // Church tax (8% or 9% of Einkommensteuer)
  let kirchensteuer = 0;
  if (inputs.kirchensteuer) {
    const state = BUNDESLAENDER.find(b => b.id === inputs.bundesland);
    const rate = state ? state.kircheRate : 0.09;
    kirchensteuer = tax * rate;
  }

  const gesamtbelastung = tax + soli + kirchensteuer;
  const netto = taxableIncome - gesamtbelastung;

  const dRate = taxableIncome > 0 ? (tax / taxableIncome) * 100 : 0;
  
  // Calculate marginal tax rate (Grenzsteuersatz) by checking tax on an additional EUR 100
  const step = 100;
  let nextTax = 0;
  if (isJoint) {
    nextTax = calculateGermanIncomeTax((taxableIncome + step) / 2, year) * 2;
  } else {
    nextTax = calculateGermanIncomeTax(taxableIncome + step, year);
  }
  const marginalRate = ((nextTax - tax) / step) * 100;

  const grundfreibetrag = year === 2026 ? 12348 : 12096;

  return {
    einkommen: taxableIncome,
    grundfreibetrag: isJoint ? grundfreibetrag * 2 : grundfreibetrag,
    steuer: Math.round(tax * 100) / 100,
    soli: Math.round(soli * 100) / 100,
    kirchensteuer: Math.round(kirchensteuer * 100) / 100,
    gesamtbelastung: Math.round(gesamtbelastung * 100) / 100,
    netto: Math.round(netto * 100) / 100,
    durchschnittssteuersatz: Math.round(dRate * 100) / 100,
    grenzsteuersatz: Math.round(marginalRate * 10) / 10,
  };
}

/**
 * Calculate Savings (Sparrechner) with monthly contributions and interest.
 */
export function calculateSparrechner(inputs: SparrechnerInputs): SparrechnerResult {
  const p = inputs.zinssatz / 100;
  const r = inputs.sparrate;
  const years = inputs.laufzeit;
  
  let balance = inputs.anfangskapital;
  let totalDeposited = inputs.anfangskapital;
  
  const yearlyData = [];

  for (let t = 1; t <= years; t++) {
    const yearDeposits = r * 12;
    totalDeposited += yearDeposits;

    if (inputs.zinseszins) {
      // Monthly interest compounding approximation
      let currentYearInterest = 0;
      let startBalance = balance;
      for (let m = 1; m <= 12; m++) {
        const monthlyZins = (balance + r) * (p / 12);
        currentYearInterest += monthlyZins;
        balance += r + monthlyZins;
      }
    } else {
      // Linear interest without compounding (interest paid out yearly or calculated on base)
      const interestOnCapital = balance * p;
      const interestOnInstallments = yearDeposits * p * 0.5; // average rate over year
      const totalInterestThisYear = interestOnCapital + interestOnInstallments;
      balance += yearDeposits + totalInterestThisYear;
    }

    const totalInterestGained = balance - totalDeposited;
    
    yearlyData.push({
      jahr: t,
      einzahlungen: Math.round(totalDeposited),
      zinsen: Math.round(totalInterestGained),
      guthaben: Math.round(balance),
    });
  }

  const totalZins = balance - totalDeposited;

  return {
    anfangskapital: inputs.anfangskapital,
    einzahlungen: Math.round(totalDeposited * 100) / 100,
    zinsgewinn: Math.round(totalZins * 100) / 100,
    endkapital: Math.round(balance * 100) / 100,
    yearlyData,
  };
}

/**
 * Tilgungsrechner / Kreditrechner: Calculates detailed mortgages and repayment plans.
 */
export function calculateKreditrechner(inputs: KreditrechnerInputs): KreditrechnerResult {
  const K = inputs.kreditsumme;
  const zSatz = inputs.zinssatz / 100; 
  const tSatz = inputs.tilgungssatz / 100;
  const years = inputs.zinsbindung;

  // Initial annual annuity rate
  const annualAnnuity = K * (zSatz + tSatz);
  const monthlyRate = annualAnnuity / 12;

  const monatlicheZinsenAnfang = (K * zSatz) / 12;
  const monatlicheTilgungAnfang = monthlyRate - monatlicheZinsenAnfang;

  let currentDebt = K;
  let accumulatedZinsen = 0;
  let accumulatedTilgung = 0;
  const yearlyPlan = [];

  for (let y = 1; y <= years; y++) {
    let yearZinsen = 0;
    let yearTilgung = 0;

    for (let m = 1; m <= 12; m++) {
      if (currentDebt <= 0) break;
      const mZinsen = currentDebt * (zSatz / 12);
      let mTilgung = monthlyRate - mZinsen;
      
      if (currentDebt < mTilgung) {
        mTilgung = currentDebt;
      }

      currentDebt -= mTilgung;
      yearZinsen += mZinsen;
      yearTilgung += mTilgung;
    }

    accumulatedZinsen += yearZinsen;
    accumulatedTilgung += yearTilgung;

    yearlyPlan.push({
      jahr: y,
      rateGesamt: Math.round(monthlyRate * 12),
      zinsAnteil: Math.round(yearZinsen),
      tilgungsAnteil: Math.round(yearTilgung),
      restschuld: Math.round(currentDebt),
    });
  }

  return {
    monatlicheRate: Math.round(monthlyRate * 100) / 100,
    zinsbindungJahre: years,
    bezahlteZinsen: Math.round(accumulatedZinsen * 100) / 100,
    getilgteSumme: Math.round(accumulatedTilgung * 100) / 100,
    restschuld: Math.round(currentDebt * 100) / 100,
    monatlicheZinsenAnfang: Math.round(monatlicheZinsenAnfang * 100) / 100,
    monatlicheTilgungAnfang: Math.round(monatlicheTilgungAnfang * 100) / 100,
    yearlyPlan,
  };
}

/**
 * Minijob and Transition Area (Midi-Job) Calculator.
 * Limits for 2025/2026: Minijob up to EUR 556/month.
 */
export function calculateMinijob(inputs: MinijobInputs): MinijobResult {
  const wage = inputs.bruttogehalt;
  const minijobLimit = 556; // 2025/2026 Limit in Germany
  const midijobLimit = 2000;

  const isMinijob = wage <= minijobLimit;
  const isMidijob = wage > minijobLimit && wage <= midijobLimit;

  let rvSatzArbeitnehmer = 0;
  let rvBeitragArbeitnehmer = 0;
  let abgabenArbeitnehmer = 0;
  let abgabenArbeitgeber = 0;

  if (isMinijob) {
    // Minijob Rules:
    // Employer pays flat share: Rentenversicherung 15%, Krankenversicherung 13%, Umlagen ~2%. Total ~30%.
    abgabenArbeitgeber = wage * 0.30;

    // Employee is pension-bound by default (pays 3.6% to reach 18.6% total RV rate with employer's 15%).
    // They can opt out (exempt).
    if (inputs.rentenversicherungspflicht) {
      rvSatzArbeitnehmer = 3.6;
      rvBeitragArbeitnehmer = wage * 0.036;
      abgabenArbeitnehmer = rvBeitragArbeitnehmer;
    } else {
      rvSatzArbeitnehmer = 0;
      rvBeitragArbeitnehmer = 0;
      abgabenArbeitnehmer = 0;
    }
  } else if (isMidijob) {
    // Midijob / \u00DCbergangsbereich (Transition area):
    // Ramps employee social share starting low up to full percentage at EUR 2,000.
    // Standard total employee share is around 20%. In transition area, it starts at ~10% and scales.
    const progressFactor = (wage - minijobLimit) / (midijobLimit - minijobLimit);
    const employeeShareRate = 0.10 + (progressFactor * 0.10); // scales 10% to 20%
    
    abgabenArbeitnehmer = wage * employeeShareRate;
    rvSatzArbeitnehmer = employeeShareRate * 100 * 0.45; // RV represents ~45% of total social contributions
    rvBeitragArbeitnehmer = abgabenArbeitnehmer * 0.45;

    // Employer pays standard social security around 19.8% in midijob
    abgabenArbeitgeber = wage * 0.198;
  } else {
    // Normal job (above midijob)
    abgabenArbeitnehmer = wage * 0.203; // Employee pays ~20.3% social contributions
    rvSatzArbeitnehmer = 9.3;
    rvBeitragArbeitnehmer = wage * 0.093;
    abgabenArbeitgeber = wage * 0.198;
  }

  const netto = wage - abgabenArbeitnehmer;
  const gesamtKostenArbeitgeber = wage + abgabenArbeitgeber;

  return {
    isMinijob,
    isMidijob,
    rvSatzArbeitnehmer: Math.round(rvSatzArbeitnehmer * 10) / 10,
    rvBeitragArbeitnehmer: Math.round(rvBeitragArbeitnehmer * 100) / 100,
    abgabenArbeitnehmer: Math.round(abgabenArbeitnehmer * 100) / 100,
    netto: Math.round(netto * 100) / 100,
    abgabenArbeitgeber: Math.round(abgabenArbeitgeber * 100) / 100,
    gesamtKostenArbeitgeber: Math.round(gesamtKostenArbeitgeber * 100) / 100,
  };
}

/**
 * Calculates German severance taxes under \u00A7 34 EStG using the F\u00FCnftelregelung (One-Fifth Rule)
 * and accounting for wage replacement benefits (Progressionsvorbehalt) and joint taxation splitting.
 */
export function calculateSeverance(inputs: SeveranceInputs): SeveranceResult {
  const { taxYear, severancePay, annualGross, wageReplacement, churchTax, jointTaxation } = inputs;
  const isJoint = jointTaxation;
  const A = severancePay;
  const zvE = annualGross;
  const E = wageReplacement;
  
  // Determine church tax rate
  let churchRate = 0;
  if (churchTax === '8%') churchRate = 0.08;
  if (churchTax === '9%') churchRate = 0.09;

  // Helper to calculate Einkommensteuer, Soli, and Church tax on base and optional replacement
  const calcTaxDetails = (incomeBase: number, replacement: number) => {
    const numFiles = isJoint ? 2 : 1;
    const incPart = incomeBase / numFiles;
    const repPart = replacement / numFiles;
    
    let tax = 0;
    if (repPart > 0) {
      // Progressionsvorbehalt calculation according to EStG rules
      const incomeForRate = incPart + repPart;
      const taxForRate = calculateGermanIncomeTax(incomeForRate, taxYear);
      // In Germany the tax rate is determined as a percentage, truncated to 4 decimal places of percentage (e.g. 15.1234%)
      const averageRatePercent = incomeForRate > 0 ? (taxForRate / incomeForRate) * 100 : 0;
      const truncatedRatePercent = Math.floor(averageRatePercent * 10000) / 10000;
      tax = incPart * (truncatedRatePercent / 100) * numFiles;
    } else {
      tax = calculateGermanIncomeTax(incPart, taxYear) * numFiles;
    }
    
    // Soli with Milderungszone (gradual phase-in) according to \u00A7 4 SolzG
    let soli = 0;
    let singleFreigrenze = 18130;
    let marriedFreigrenze = 36260;
    if (taxYear === 2025) {
      singleFreigrenze = 19950;
      marriedFreigrenze = 39900;
    } else if (taxYear === 2026) {
      singleFreigrenze = 20340;
      marriedFreigrenze = 40680;
    }
    const soliFreigrenze = isJoint ? marriedFreigrenze : singleFreigrenze;
    if (tax > soliFreigrenze) {
      soli = Math.min(tax * 0.055, (tax - soliFreigrenze) * 0.119);
    }
    
    // Church tax
    const churchTaxVal = tax * churchRate;
    
    return {
      tax,
      soli,
      churchTax: churchTaxVal,
      total: tax + soli + churchTaxVal
    };
  };

  // 1. T1: Tax on regular income (without severance)
  const T1 = calcTaxDetails(zvE, E);

  // 2. T_full: Standard taxation WITH severance (No F\u00FCnftelregelung)
  const T_full = calcTaxDetails(zvE + A, E);

  // 3. One-fifth calculation for F\u00FCnftelregelung (T2)
  // We calculate tax on (zvE + Severance/5) with Progression
  const T_fifth_base = calcTaxDetails(zvE + A / 5, E);
  
  // The tax on severance under F\u00FCnftelregelung is 5 * (Tax(zvE + A/5) - Tax(zvE))
  const taxOnSeverance = Math.max(0, 5 * (T_fifth_base.tax - T1.tax));
  const totalSeveranceIncomeTax = T1.tax + taxOnSeverance;

  // Solidarit\u00E4tszuschlag
  let soliWithFifth = 0;
  let singleFreigrenze = 18130;
  let marriedFreigrenze = 36260;
  if (taxYear === 2025) {
    singleFreigrenze = 19950;
    marriedFreigrenze = 39900;
  } else if (taxYear === 2026) {
    singleFreigrenze = 20340;
    marriedFreigrenze = 40680;
  }
  const soliFreigrenze = isJoint ? marriedFreigrenze : singleFreigrenze;
  if (totalSeveranceIncomeTax > soliFreigrenze) {
    soliWithFifth = Math.min(totalSeveranceIncomeTax * 0.055, (totalSeveranceIncomeTax - soliFreigrenze) * 0.119);
  }
  const soliOnSeverance = Math.max(0, soliWithFifth - T1.soli);

  // Church Tax
  const churchTaxOnSeverance = taxOnSeverance * churchRate;
  const churchTaxWithFifth = T1.churchTax + churchTaxOnSeverance;

  // Totals under F\u00FCnftelregelung
  const totalTaxWithFifth = totalSeveranceIncomeTax + soliWithFifth + churchTaxWithFifth;
  const totalTaxOnSeverancePay = taxOnSeverance + soliOnSeverance + churchTaxOnSeverance;

  // Comparison & Savings
  const taxSavings = Math.max(0, T_full.total - totalTaxWithFifth);
  const netSeverancePay = A - totalTaxOnSeverancePay;
  const effectiveSeveranceTaxRate = A > 0 ? (totalTaxOnSeverancePay / A) * 100 : 0;

  // Grand totals
  const grandTotalTax = totalSeveranceIncomeTax;
  const grandTotalSoli = soliWithFifth;
  const grandTotalChurchTax = churchTaxWithFifth;
  const grandTotalPayment = totalTaxWithFifth;
  
  const totalIncomeAll = zvE + A;
  const totalNetAll = totalIncomeAll - grandTotalPayment;
  const grandTotalNetRatio = totalIncomeAll > 0 ? (totalNetAll / totalIncomeAll) * 100 : 0;

  return {
    taxYear,
    severancePay: A,
    annualGross: zvE,
    wageReplacement: E,
    isJoint,
    
    taxNormal: Math.round(T1.tax * 100) / 100,
    soliNormal: Math.round(T1.soli * 100) / 100,
    churchTaxNormal: Math.round(T1.churchTax * 100) / 100,
    totalTaxNormal: Math.round(T1.total * 100) / 100,
    
    taxStandard: Math.round(T_full.tax * 100) / 100,
    soliStandard: Math.round(T_full.soli * 100) / 100,
    churchTaxStandard: Math.round(T_full.churchTax * 100) / 100,
    totalTaxStandard: Math.round(T_full.total * 100) / 100,
    addedTaxStandard: Math.round((T_full.total - T1.total) * 100) / 100,
    
    taxWithFifth: Math.round(totalSeveranceIncomeTax * 100) / 100,
    soliWithFifth: Math.round(soliWithFifth * 100) / 100,
    churchTaxWithFifth: Math.round(churchTaxWithFifth * 100) / 100,
    totalTaxWithFifth: Math.round(totalTaxWithFifth * 100) / 100,
    
    taxOnSeverancePay: Math.round(taxOnSeverance * 100) / 100,
    soliOnSeverancePay: Math.round(soliOnSeverance * 100) / 100,
    churchTaxOnSeverancePay: Math.round(churchTaxOnSeverance * 100) / 100,
    totalTaxOnSeverancePay: Math.round(totalTaxOnSeverancePay * 100) / 100,
    
    taxSavings: Math.round(taxSavings * 100) / 100,
    netSeverancePay: Math.round(netSeverancePay * 100) / 100,
    effectiveSeveranceTaxRate: Math.round(effectiveSeveranceTaxRate * 100) / 100,
    
    grandTotalTax: Math.round(grandTotalTax * 100) / 100,
    grandTotalSoli: Math.round(grandTotalSoli * 100) / 100,
    grandTotalChurchTax: Math.round(grandTotalChurchTax * 100) / 100,
    grandTotalPayment: Math.round(grandTotalPayment * 100) / 100,
    grandTotalNetRatio: Math.round(grandTotalNetRatio * 100) / 100,
  };
}

/**
  * Abgeltungssteuer-Rechner (Sec.  32d EStG, Sec.  20 InvStG)
  * Calculates capital gains tax, partial exemption (Teilfreistellung),
  * Sparer-Pauschbetrag, Soli (5.5%), Church tax (8%/9%), and Guenstigerpruefung.
  */
export function calculateAbgeltungssteuer(inputs: AbgeltungssteuerInputs): AbgeltungssteuerResult {
  const kapitalertrag = Math.max(0, inputs.kapitalertrag);
  
  // 1. Teilfreistellung according to Sec.  20 InvStG
  let teilfreistellungProzent = 0;
  if (inputs.assetType === 'mischfonds') {
    teilfreistellungProzent = 15;
  } else if (inputs.assetType === 'aktienfonds') {
    teilfreistellungProzent = 30;
  } else if (inputs.assetType === 'immo_inland') {
    teilfreistellungProzent = 60;
  } else if (inputs.assetType === 'immo_ausland') {
    teilfreistellungProzent = 80;
  }

  const teilfreistellungBetrag = kapitalertrag * (teilfreistellungProzent / 100);
  const ertragNachTeilfreistellung = Math.max(0, kapitalertrag - teilfreistellungBetrag);

  // 2. Sparer-Pauschbetrag (Sec.  20 Abs. 9 EStG)
  const sparerpauschbetragAngewendet = Math.min(ertragNachTeilfreistellung, Math.max(0, inputs.sparerpauschbetrag));
  const zuVersteuernderErtrag = Math.max(0, ertragNachTeilfreistellung - sparerpauschbetragAngewendet);

  // 3. Abgeltungssteuer with / without Church Tax formula (Sec.  32d Abs. 1 EStG)
  // Standard without church tax = 25.0%
  // With 8% church tax = zuVersteuernderErtrag / 4.08 -> nominal 24.50980%
  // With 9% church tax = zuVersteuernderErtrag / 4.09 -> nominal 24.44990%
  let abgeltungssteuerSatz = 25.0;
  let abgeltungssteuer = 0;
  let kirchensteuer = 0;

  if (inputs.churchTax === '8%') {
    abgeltungssteuerSatz = 25 / 1.02; // 24.50980...
    abgeltungssteuer = zuVersteuernderErtrag / 4.08;
    kirchensteuer = abgeltungssteuer * 0.08;
  } else if (inputs.churchTax === '9%') {
    abgeltungssteuerSatz = 25 / 1.0225; // 24.44990...
    abgeltungssteuer = zuVersteuernderErtrag / 4.09;
    kirchensteuer = abgeltungssteuer * 0.09;
  } else {
    abgeltungssteuerSatz = 25.0;
    abgeltungssteuer = zuVersteuernderErtrag * 0.25;
    kirchensteuer = 0;
  }

  // 4. Solidaritaetszuschlag (5.5% of Abgeltungssteuer)
  const soli = abgeltungssteuer * 0.055;

  // 5. Total Tax & Net Return
  const gesamtSteuer = abgeltungssteuer + soli + kirchensteuer;
  const nettoErtrag = kapitalertrag - gesamtSteuer;
  const effektiverSteuersatz = kapitalertrag > 0 ? (gesamtSteuer / kapitalertrag) * 100 : 0;

  // 6. Guenstigerpruefung check (Sec.  32d Abs. 6 EStG)
  // If personal marginal tax rate is lower than 25%, filing Anlage KAP lowers tax burden
  let guenstigerpruefungEmpfehlung = false;
  let steuerBeiPersoenlichemSatz = 0;
  let ersparnisGuenstigerpruefung = 0;

  if (inputs.personalTaxRate !== undefined && inputs.personalTaxRate >= 0) {
    const personalRateDec = inputs.personalTaxRate / 100;
    const personalBaseTax = zuVersteuernderErtrag * personalRateDec;
    const personalSoli = personalBaseTax * 0.055;
    let personalKiSt = 0;
    if (inputs.churchTax === '8%') personalKiSt = personalBaseTax * 0.08;
    if (inputs.churchTax === '9%') personalKiSt = personalBaseTax * 0.09;

    steuerBeiPersoenlichemSatz = personalBaseTax + personalSoli + personalKiSt;
    
    if (inputs.personalTaxRate < 25 && zuVersteuernderErtrag > 0) {
      guenstigerpruefungEmpfehlung = true;
      ersparnisGuenstigerpruefung = Math.max(0, gesamtSteuer - steuerBeiPersoenlichemSatz);
    }
  }

  return {
    kapitalertrag: Math.round(kapitalertrag * 100) / 100,
    teilfreistellungProzent,
    teilfreistellungBetrag: Math.round(teilfreistellungBetrag * 100) / 100,
    ertragNachTeilfreistellung: Math.round(ertragNachTeilfreistellung * 100) / 100,
    sparerpauschbetragAngewendet: Math.round(sparerpauschbetragAngewendet * 100) / 100,
    zuVersteuernderErtrag: Math.round(zuVersteuernderErtrag * 100) / 100,
    abgeltungssteuerSatz: Math.round(abgeltungssteuerSatz * 100000) / 100000,
    abgeltungssteuer: Math.round(abgeltungssteuer * 100) / 100,
    soli: Math.round(soli * 100) / 100,
    kirchensteuer: Math.round(kirchensteuer * 100) / 100,
    gesamtSteuer: Math.round(gesamtSteuer * 100) / 100,
    effektiverSteuersatz: Math.round(effektiverSteuersatz * 100) / 100,
    nettoErtrag: Math.round(nettoErtrag * 100) / 100,
    guenstigerpruefungEmpfehlung,
    steuerBeiPersoenlichemSatz: Math.round(steuerBeiPersoenlichemSatz * 100) / 100,
    ersparnisGuenstigerpruefung: Math.round(ersparnisGuenstigerpruefung * 100) / 100,
  };
}

/**
 * Calculates extraordinary burdens (Außergewöhnliche Belastungen) under § 33 EStG.
 * Incorporates the landmark Federal Fiscal Court decision (BFH-Urteil vom 19.01.2017, VI R 75/14)
 * applying progressive graduated rate tiers (stufenweise Berechnung) instead of a harsh flat jump.
 */
export function calculateAussergewoehnlicheBelastungen(
  inputs: AussergewoehnlicheBelastungenInputs
): AussergewoehnlicheBelastungenResult {
  const expenses = Math.max(0, inputs.expenses || 0);
  const income = Math.max(0, inputs.income || 0);
  const taxYear = inputs.taxYear || 2027;
  const familyStatus = inputs.familyStatus || 'single';
  const childrenCategory = inputs.childrenCategory || '1-2';
  const churchTaxRate = inputs.churchTaxRate || 0;

  // Tier Thresholds under § 33 Abs. 3 EStG
  const TIER1_MAX = 15340;
  const TIER2_MAX = 51130;

  // Determine percentage rates based on family status & child count
  let r1 = 0.05;
  let r2 = 0.06;
  let r3 = 0.07;

  if (childrenCategory === '3+') {
    r1 = 0.01;
    r2 = 0.01;
    r3 = 0.02;
  } else if (childrenCategory === '1-2') {
    r1 = 0.02;
    r2 = 0.03;
    r3 = 0.04;
  } else {
    // 0 children
    if (familyStatus === 'married' || familyStatus === 'single_parent') {
      r1 = 0.04;
      r2 = 0.05;
      r3 = 0.06;
    } else {
      // Single without children
      r1 = 0.05;
      r2 = 0.06;
      r3 = 0.07;
    }
  }

  // Graduated tiered calculation (BFH VI R 75/14)
  const part1 = Math.min(income, TIER1_MAX);
  const part2 = Math.max(0, Math.min(income, TIER2_MAX) - TIER1_MAX);
  const part3 = Math.max(0, income - TIER2_MAX);

  const tier1Amount = Math.round(part1 * r1 * 100) / 100;
  const tier2Amount = Math.round(part2 * r2 * 100) / 100;
  const tier3Amount = Math.round(part3 * r3 * 100) / 100;

  const zumutbareBelastung = Math.round((tier1Amount + tier2Amount + tier3Amount) * 100) / 100;
  const deductibleAmount = Math.max(0, Math.round((expenses - zumutbareBelastung) * 100) / 100);
  const isOverLimit = deductibleAmount > 0;

  const tiers: StufenDetail[] = [
    {
      tierName: 'Stufe 1 (bis 15.340 \u20AC)',
      baseAmount: part1,
      rate: r1 * 100,
      tierAmount: tier1Amount,
    },
    {
      tierName: 'Stufe 2 (15.341 \u20AC bis 51.130 \u20AC)',
      baseAmount: part2,
      rate: r2 * 100,
      tierAmount: tier2Amount,
    },
    {
      tierName: 'Stufe 3 (\u00FCber 51.130 \u20AC)',
      baseAmount: part3,
      rate: r3 * 100,
      tierAmount: tier3Amount,
    },
  ];

  // Helper to compute income tax, soli, and church tax
  const calcTaxProfile = (zvE: number, isSplitting: boolean) => {
    let est = 0;
    if (isSplitting) {
      est = 2 * calculateGermanIncomeTax(Math.floor(zvE / 2), taxYear);
    } else {
      est = calculateGermanIncomeTax(zvE, taxYear);
    }

    const soliFreigrenze = isSplitting ? 36260 : 18130;
    let soli = 0;
    if (est > soliFreigrenze) {
      const rawSoli = est * 0.055;
      const milderung = (est - soliFreigrenze) * 0.119;
      soli = Math.min(rawSoli, milderung);
    }
    soli = Math.max(0, Math.floor(soli * 100) / 100);

    const church = Math.max(0, Math.floor(est * (churchTaxRate / 100) * 100) / 100);
    const total = est + soli + church;

    return { est, soli, church, total };
  };

  // Profile Grundtarif (Einzelveranlagung)
  const beforeGrund = calcTaxProfile(income, false);
  const afterGrund = calcTaxProfile(Math.max(0, income - deductibleAmount), false);
  const taxSavingsGrund = Math.max(0, Math.round((beforeGrund.total - afterGrund.total) * 100) / 100);

  const deltaGrund = calcTaxProfile(income + 100, false).total - beforeGrund.total;
  const marginalRateGrund = Math.min(50, Math.max(0, Math.round((deltaGrund / 100) * 1000) / 10));

  // Profile Splittingtarif (Zusammenveranlagung)
  const beforeSplitting = calcTaxProfile(income, true);
  const afterSplitting = calcTaxProfile(Math.max(0, income - deductibleAmount), true);
  const taxSavingsSplitting = Math.max(0, Math.round((beforeSplitting.total - afterSplitting.total) * 100) / 100);

  const deltaSplitting = calcTaxProfile(income + 100, true).total - beforeSplitting.total;
  const marginalRateSplitting = Math.min(50, Math.max(0, Math.round((deltaSplitting / 100) * 1000) / 10));

  return {
    expenses,
    income,
    taxYear,
    familyStatus,
    childrenCategory,
    churchTaxRate,

    zumutbareBelastung,
    tiers,

    deductibleAmount,
    isOverLimit,

    taxBeforeGrund: beforeGrund.est,
    soliBeforeGrund: beforeGrund.soli,
    churchBeforeGrund: beforeGrund.church,
    totalTaxBeforeGrund: beforeGrund.total,

    taxAfterGrund: afterGrund.est,
    soliAfterGrund: afterGrund.soli,
    churchAfterGrund: afterGrund.church,
    totalTaxAfterGrund: afterGrund.total,

    taxSavingsGrund,
    marginalRateGrund,

    taxBeforeSplitting: beforeSplitting.est,
    soliBeforeSplitting: beforeSplitting.soli,
    churchBeforeSplitting: beforeSplitting.church,
    totalTaxBeforeSplitting: beforeSplitting.total,

    taxAfterSplitting: afterSplitting.est,
    soliAfterSplitting: afterSplitting.soli,
    churchAfterSplitting: afterSplitting.church,
    totalTaxAfterSplitting: afterSplitting.total,

    taxSavingsSplitting,
    marginalRateSplitting,
  };
}

/**
  * Forward calculation of Net salary from Gross salary according to German tax law & social insurance rules.
  */
export function calculateBruttoNettoForward(
  bruttogehalt: number,
  inputs: Pick<BruttoRechnerInputs, 
    'jahr' | 'geburtsjahr' | 'kinderfreibetrag' | 'steuerklasse' | 
    'kinderStatus' | 'bundesland' | 'kirchensteuer' | 
    'rentenversicherungspflicht' | 'arbeitslosenversicherungspflicht' | 
    'krankenversicherung' | 'zusatzbeitrag' | 'freibetragLSt' | 
    'hinzurechnungsbetrag' | 'privateKVBeitrag' | 'umlagen'
  >
) {
  const jahr = inputs.jahr || 2027;
  const is2027 = jahr >= 2027;
  const is2026 = jahr === 2026;
  const jahresBrutto = Math.max(0, bruttogehalt * 12);

  // Altersentlastungsbetrag (§ 24a EStG)
  let altersentlastung = 0;
  if (inputs.geburtsjahr === 'vor 1962') {
    const rate = is2027 ? 0.124 : (is2026 ? 0.128 : 0.132);
    const maxClaim = is2027 ? 589 : (is2026 ? 608 : 627);
    altersentlastung = Math.min(jahresBrutto * rate, maxClaim);
  }

  // Beitragsbemessungsgrenzen (monatlich)
  const bbgRV = is2027 ? 8750 : (is2026 ? 8450 : 7550);
  const bbgKV = is2027 ? 6000 : (is2026 ? 5812.5 : 5175);

  const baseRV = Math.min(bruttogehalt, bbgRV);
  const baseKV = Math.min(bruttogehalt, bbgKV);

  // 1. Rentenversicherung (RV)
  const rvSatz = inputs.rentenversicherungspflicht === 'ja' ? 0.093 : 0;
  const rvBeitrag = baseRV * rvSatz;

  // 2. Arbeitslosenversicherung (AV)
  const avSatz = inputs.arbeitslosenversicherungspflicht === 'ja' ? 0.013 : 0;
  const avBeitrag = baseRV * avSatz;

  // 3. Krankenversicherung (KV)
  let kvBeitrag = 0;
  const zusatz = inputs.zusatzbeitrag ?? 2.9;
  if (inputs.krankenversicherung === 'gesetzlich') {
    const kvSatz = 0.073 + (zusatz / 200);
    kvBeitrag = baseKV * kvSatz;
  } else if (inputs.krankenversicherung === 'erm\u00E4\u00DFigt') {
    const kvSatz = 0.070 + (zusatz / 200);
    kvBeitrag = baseKV * kvSatz;
  } else {
    kvBeitrag = inputs.privateKVBeitrag || 0;
  }

  // 4. Pflegeversicherung (PV)
  const isSaxon = inputs.bundesland === 'SN';
  const pvBase = isSaxon ? 0.023 : 0.018;
  let pvMod = 0;
  if (inputs.kinderStatus === 'kinderlos_ab_23') pvMod = 0.006;
  else if (inputs.kinderStatus === 'eltern_2') pvMod = -0.0025;
  else if (inputs.kinderStatus === 'eltern_3') pvMod = -0.005;
  else if (inputs.kinderStatus === 'eltern_4') pvMod = -0.0075;
  else if (inputs.kinderStatus === 'eltern_5_plus') pvMod = -0.01;

  const pvSatz = pvBase + pvMod;
  const pvBeitrag = baseKV * pvSatz;

  const sozialabgaben = rvBeitrag + avBeitrag + kvBeitrag + pvBeitrag;

  // Steuern (Lohnsteuer nach § 32a EStG)
  const werbungskosten = 1230;
  let vorsorge = Math.min(20000, jahresBrutto * 0.2);

  let abzugFreibetrag = 0;
  const gfb = is2027 ? 12552 : (is2026 ? 12348 : 12096);
  if (inputs.steuerklasse === 1 || inputs.steuerklasse === 4) {
    abzugFreibetrag = 0;
  } else if (inputs.steuerklasse === 2) {
    abzugFreibetrag = 4260; // Alleinerziehend
  } else if (inputs.steuerklasse === 3) {
    abzugFreibetrag = gfb; // Verheiratet Besserverdiener
  } else if (inputs.steuerklasse === 5) {
    abzugFreibetrag = -8000;
  } else if (inputs.steuerklasse === 6) {
    abzugFreibetrag = -gfb;
  }

  const customAllowance = ((inputs.freibetragLSt || 0) * 12) - ((inputs.hinzurechnungsbetrag || 0) * 12);
  let zvEJahr = jahresBrutto - werbungskosten - vorsorge - abzugFreibetrag - altersentlastung - customAllowance;

  const kinderZahl = parseFloat(inputs.kinderfreibetrag || '0');
  if (kinderZahl > 0) {
    const kinderFreibetragAmt = kinderZahl * (is2027 ? 4956 : (is2026 ? 4878 : 4656));
    zvEJahr -= kinderFreibetragAmt * 0.3;
  }
  if (zvEJahr < 0) zvEJahr = 0;

  const annualLSt = calculateGermanIncomeTax(zvEJahr, jahr);
  const lohnsteuer = annualLSt / 12;

  // Solidaritätszuschlag
  const singleFreigrenze = is2027 ? 20700 : (is2026 ? 20340 : 19950);
  const marriedFreigrenze = is2027 ? 41400 : (is2026 ? 40680 : 39900);
  const soliFreigrenze = inputs.steuerklasse === 3 ? marriedFreigrenze : singleFreigrenze;

  let annualSoli = 0;
  if (annualLSt > soliFreigrenze) {
    annualSoli = Math.min(annualLSt * 0.055, (annualLSt - soliFreigrenze) * 0.119);
  }
  const soli = Math.max(0, annualSoli / 12);

  // Kirchensteuer
  let kirchensteuer = 0;
  if (inputs.kirchensteuer === 'ja') {
    const isEightPercent = inputs.bundesland === 'BY' || inputs.bundesland === 'BW';
    kirchensteuer = lohnsteuer * (isEightPercent ? 0.08 : 0.09);
  }

  const steuern = lohnsteuer + soli + kirchensteuer;
  const netto = bruttogehalt - sozialabgaben - steuern;

  // Arbeitgeber-Kosten
  const rvAG = baseRV * 0.093;
  const avAG = baseRV * 0.013;
  const kvAG = (inputs.krankenversicherung === 'gesetzlich' || inputs.krankenversicherung === 'erm\u00E4\u00DFigt')
    ? baseKV * (inputs.krankenversicherung === 'gesetzlich' ? 0.073 + (zusatz / 200) : 0.070 + (zusatz / 200))
    : 0;
  const pvAG = baseKV * (isSaxon ? 0.013 : 0.018);
  const umlagen = inputs.umlagen === 'ja' ? bruttogehalt * 0.012 : 0;
  const arbeitgeberAnteil = rvAG + avAG + kvAG + pvAG + umlagen;

  return {
    netto,
    brutto: bruttogehalt,
    lohnsteuer,
    soli,
    kirchensteuer,
    steuern,
    rvBeitrag,
    avBeitrag,
    kvBeitrag,
    pvBeitrag,
    sozialabgaben,
    arbeitgeberAnteil,
    gesamtKostenArbeitgeber: bruttogehalt + arbeitgeberAnteil,
  };
}

/**
 * Reverse calculation engine: Computes required Gross salary from desired Net salary
 * using binary search bisection to sub-cent mathematical precision.
 */
export function solveBruttoFromNetto(inputs: BruttoRechnerInputs): BruttoRechnerResult {
  const targetMonthlyNetto = inputs.zeitraum === 'jahr' 
    ? (inputs.wunschNetto || 0) / 12 
    : (inputs.wunschNetto || 0);

  if (targetMonthlyNetto <= 0) {
    return {
      benoetigtesBruttoMonat: 0,
      benoetigtesBruttoJahr: 0,
      wunschNettoMonat: 0,
      wunschNettoJahr: 0,
      lohnsteuerMonat: 0,
      lohnsteuerJahr: 0,
      soliMonat: 0,
      soliJahr: 0,
      kirchensteuerMonat: 0,
      kirchensteuerJahr: 0,
      steuernGesamtMonat: 0,
      steuernGesamtJahr: 0,
      rentenversicherungMonat: 0,
      rentenversicherungJahr: 0,
      arbeitslosenversicherungMonat: 0,
      arbeitslosenversicherungJahr: 0,
      krankenversicherungMonat: 0,
      krankenversicherungJahr: 0,
      pflegeversicherungMonat: 0,
      pflegeversicherungJahr: 0,
      sozialabgabenGesamtMonat: 0,
      sozialabgabenGesamtJahr: 0,
      abzuegeGesamtMonat: 0,
      abzuegeGesamtJahr: 0,
      nettoQuote: 100,
      abzugsQuote: 0,
      arbeitgeberAnteilMonat: 0,
      gesamtKostenArbeitgeberMonat: 0,
      gesamtKostenArbeitgeberJahr: 0,
      stundenlohnBrutto: 0,
      stundenlohnNetto: 0,
      verhandlung: {
        basisBrutto: 0,
        puffer5: 0,
        puffer10: 0,
        puffer15: 0,
      }
    };
  }

  // Binary search bounds
  let low = 0;
  let high = Math.max(250000, targetMonthlyNetto * 4.5);

  for (let iter = 0; iter < 45; iter++) {
    const mid = (low + high) / 2;
    const { netto } = calculateBruttoNettoForward(mid, inputs);
    if (netto < targetMonthlyNetto) {
      low = mid;
    } else {
      high = mid;
    }
  }

  const finalBrutto = (low + high) / 2;
  const detail = calculateBruttoNettoForward(finalBrutto, inputs);

  const bruttoMonat = Math.round(finalBrutto * 100) / 100;
  const bruttoJahr = Math.round(bruttoMonat * 12 * 100) / 100;

  const nettoMonat = Math.round(detail.netto * 100) / 100;
  const nettoJahr = Math.round(nettoMonat * 12 * 100) / 100;

  const lstMonat = Math.round(detail.lohnsteuer * 100) / 100;
  const lstJahr = Math.round(lstMonat * 12 * 100) / 100;

  const soliMonat = Math.round(detail.soli * 100) / 100;
  const soliJahr = Math.round(soliMonat * 12 * 100) / 100;

  const kistMonat = Math.round(detail.kirchensteuer * 100) / 100;
  const kistJahr = Math.round(kistMonat * 12 * 100) / 100;

  const steuernMonat = Math.round(detail.steuern * 100) / 100;
  const steuernJahr = Math.round(steuernMonat * 12 * 100) / 100;

  const rvMonat = Math.round(detail.rvBeitrag * 100) / 100;
  const rvJahr = Math.round(rvMonat * 12 * 100) / 100;

  const avMonat = Math.round(detail.avBeitrag * 100) / 100;
  const avJahr = Math.round(avMonat * 12 * 100) / 100;

  const kvMonat = Math.round(detail.kvBeitrag * 100) / 100;
  const kvJahr = Math.round(kvMonat * 12 * 100) / 100;

  const pvMonat = Math.round(detail.pvBeitrag * 100) / 100;
  const pvJahr = Math.round(pvMonat * 12 * 100) / 100;

  const sozMonat = Math.round(detail.sozialabgaben * 100) / 100;
  const sozJahr = Math.round(sozMonat * 12 * 100) / 100;

  const abzuegeMonat = Math.round((steuernMonat + sozMonat) * 100) / 100;
  const abzuegeJahr = Math.round(abzuegeMonat * 12 * 100) / 100;

  const agAnteilMonat = Math.round(detail.arbeitgeberAnteil * 100) / 100;
  const agGesamtMonat = Math.round((bruttoMonat + agAnteilMonat) * 100) / 100;
  const agGesamtJahr = Math.round(agGesamtMonat * 12 * 100) / 100;

  const nettoQuote = bruttoMonat > 0 ? Math.round((nettoMonat / bruttoMonat) * 1000) / 10 : 0;
  const abzugsQuote = bruttoMonat > 0 ? Math.round((abzuegeMonat / bruttoMonat) * 1000) / 10 : 0;

  const wochenstunden = inputs.wochenstunden || 40;
  const monthlyHours = wochenstunden * 4.345;
  const stundenlohnBrutto = monthlyHours > 0 ? Math.round((bruttoMonat / monthlyHours) * 100) / 100 : 0;
  const stundenlohnNetto = monthlyHours > 0 ? Math.round((nettoMonat / monthlyHours) * 100) / 100 : 0;

  return {
    benoetigtesBruttoMonat: bruttoMonat,
    benoetigtesBruttoJahr: bruttoJahr,
    wunschNettoMonat: nettoMonat,
    wunschNettoJahr: nettoJahr,
    lohnsteuerMonat: lstMonat,
    lohnsteuerJahr: lstJahr,
    soliMonat: soliMonat,
    soliJahr: soliJahr,
    kirchensteuerMonat: kistMonat,
    kirchensteuerJahr: kistJahr,
    steuernGesamtMonat: steuernMonat,
    steuernGesamtJahr: steuernJahr,
    rentenversicherungMonat: rvMonat,
    rentenversicherungJahr: rvJahr,
    arbeitslosenversicherungMonat: avMonat,
    arbeitslosenversicherungJahr: avJahr,
    krankenversicherungMonat: kvMonat,
    krankenversicherungJahr: kvJahr,
    pflegeversicherungMonat: pvMonat,
    pflegeversicherungJahr: pvJahr,
    sozialabgabenGesamtMonat: sozMonat,
    sozialabgabenGesamtJahr: sozJahr,
    abzuegeGesamtMonat: abzuegeMonat,
    abzuegeGesamtJahr: abzuegeJahr,
    nettoQuote,
    abzugsQuote,
    arbeitgeberAnteilMonat: agAnteilMonat,
    gesamtKostenArbeitgeberMonat: agGesamtMonat,
    gesamtKostenArbeitgeberJahr: agGesamtJahr,
    stundenlohnBrutto,
    stundenlohnNetto,
    verhandlung: {
      basisBrutto: bruttoMonat,
      puffer5: Math.round(bruttoMonat * 1.05),
      puffer10: Math.round(bruttoMonat * 1.10),
      puffer15: Math.round(bruttoMonat * 1.15),
    }
  };
}

/**
 * German Inheritance Tax (Erbschaftsteuer) Calculation Engine according to ErbStG and BewG.
 * Updated with official parameters for 2025, 2026, and 2027:
 * - § 10 Abs. 5 Nr. 3: Updated Pauschbetrag für Erbfallkosten of 15.000 € (increased from 10.300 €)
 * - § 13 Abs. 1 Nr. 4b/4c: Familienheim self-use exemption (10 years)
 * - § 13 Abs. 1 Nr. 1: Hausrat- und Freibetragsregelungen
 * - § 13d (vormals § 13c): 10 % Bewertungsabschlag für vermietete Wohnimmobilien
 * - §§ 13a, 13b: Betriebsvermögen Regel- und Optionsverschonung mit gleitendem Abzugsbetrag
 * - § 14: Berücksichtigung von Vorerwerben innerhalb von 10 Jahren
 * - § 15: Steuerklassen I, II, III
 * - § 16: Persönliche Freibeträge
 * - § 17: Versorgungsfreibetrag für Ehegatten (256.000 €) und Kinder (gestaffelt bis 52.000 €)
 * - § 19: Steuertarifstufen (7 % bis 50 %)
 * - § 19 Abs. 3: Gesetzlicher Härteausgleich zur Vermeidung von Stufensprüngen
 * - § 19a: Tarifentlastung für Betriebsvermögen bei Steuerklasse II und III
 */
export function calculateErbschaftsteuer(inputs: ErbschaftsteuerInputs): ErbschaftsteuerResult {
  // 1. Determine Steuerklasse and Personal Allowance according to §§ 15, 16 ErbStG
  let steuerklasse: 'I' | 'II' | 'III' = 'I';
  let steuerklasseNum = 1;
  let persoenlicherFreibetrag = 500000;

  switch (inputs.verwandtschaft) {
    case 'ehegatte':
    case 'lebenspartner':
      steuerklasse = 'I';
      steuerklasseNum = 1;
      persoenlicherFreibetrag = 500000;
      break;
    case 'kind':
    case 'stiefkind':
      steuerklasse = 'I';
      steuerklasseNum = 1;
      persoenlicherFreibetrag = 400000;
      break;
    case 'enkel':
      steuerklasse = 'I';
      steuerklasseNum = 1;
      // If parents (child of deceased) already passed away, allowance is 400.000 € (§ 16 Abs. 1 Nr. 3 ErbStG)
      persoenlicherFreibetrag = inputs.enkelElternteilVerstorben ? 400000 : 200000;
      break;
    case 'urenkel':
      steuerklasse = 'I';
      steuerklasseNum = 1;
      persoenlicherFreibetrag = 100000;
      break;
    case 'eltern':
    case 'grosseltern':
      // Im Erbfall (Erwerb von Todes wegen) Steuerklasse I, Freibetrag 100.000 € (§ 15 Abs. 1, § 16 Abs. 1 Nr. 5 ErbStG)
      steuerklasse = 'I';
      steuerklasseNum = 1;
      persoenlicherFreibetrag = 100000;
      break;
    case 'geschwister':
    case 'nichten_neffen':
    case 'stiefeltern':
    case 'schwiegereltern':
    case 'schwiegerkinder':
    case 'geschiedener_ehegatte':
      steuerklasse = 'II';
      steuerklasseNum = 2;
      persoenlicherFreibetrag = 20000;
      break;
    case 'nicht_verwandt':
    default:
      steuerklasse = 'III';
      steuerklasseNum = 3;
      persoenlicherFreibetrag = 20000;
      break;
  }

  // 2. Gross Estate Assets (Aktiva)
  const privatvermoegen = Math.max(0, inputs.privatvermoegen || 0);
  const hausrat = Math.max(0, inputs.hausrat || 0);
  const immobilienWert = Math.max(0, inputs.immobilienWert || 0);
  const betriebsvermoegen = Math.max(0, inputs.betriebsvermoegen || 0);
  const bruttoNachlass = privatvermoegen + hausrat + immobilienWert + betriebsvermoegen;

  // 3. Hausrat- und Befreiungsregelung (§ 13 Abs. 1 Nr. 1 ErbStG)
  let steuerfreierHausrat = 0;
  if (steuerklasse === 'I') {
    // Bis zu 41.000 € Hausrat + bis zu 12.000 € andere bewegliche Gegenstände (zusammen max 53.000 €)
    steuerfreierHausrat = Math.min(hausrat, 53000);
  } else {
    // Steuerklasse II & III: Hausrat und andere Gegenstände insgesamt bis zu 12.000 €
    steuerfreierHausrat = Math.min(hausrat, 12000);
  }

  // 4. Immobilienbefreiung & Bewertungsabschlag (§ 13 Abs. 1 Nr. 4b/4c und § 13d ErbStG)
  let steuerfreieImmobilie = 0;
  if (inputs.immobilienNutzung === 'familienheim_selbst') {
    if (inputs.verwandtschaft === 'ehegatte' || inputs.verwandtschaft === 'lebenspartner') {
      // 100% steuerfrei, keine Flächenbeschränkung bei 10 Jahren Selbstnutzung (§ 13 Abs. 1 Nr. 4b)
      steuerfreieImmobilie = immobilienWert;
    } else if (
      inputs.verwandtschaft === 'kind' || 
      inputs.verwandtschaft === 'stiefkind' || 
      (inputs.verwandtschaft === 'enkel' && inputs.enkelElternteilVerstorben)
    ) {
      // Steuerfrei bis 200 m² Wohnfläche (§ 13 Abs. 1 Nr. 4c)
      const flaeche = inputs.immobilienWohnflaeche || 0;
      if (flaeche <= 200 || flaeche === 0) {
        steuerfreieImmobilie = immobilienWert;
      } else {
        steuerfreieImmobilie = Math.round(immobilienWert * (200 / flaeche));
      }
    } else {
      // Für andere Verwandte / Dritte gilt die Familienheim-Befreiung gesetzlich nicht
      steuerfreieImmobilie = 0;
    }
  } else if (inputs.immobilienNutzung === 'vermietet_wohnzwecke') {
    // 10 % gesetzlicher Bewertungsabschlag nach § 13d ErbStG (früher § 13c)
    steuerfreieImmobilie = Math.round(immobilienWert * 0.10);
  } else {
    steuerfreieImmobilie = 0;
  }

  // 5. Betriebsvermögen Verschonungsabschlag & gleitender Abzugsbetrag (§§ 13a, 13b ErbStG)
  let steuerfreiesBetriebsvermoegen = 0;
  let verbleibendesSteuerpflichtigesBetriebsvermoegen = 0;

  if (inputs.betriebsvermoegenOption === 'optionsverschonung') {
    steuerfreiesBetriebsvermoegen = betriebsvermoegen; // 100 % Verschonungsabschlag
  } else if (inputs.betriebsvermoegenOption === 'regelverschonung') {
    const abschlag85 = betriebsvermoegen * 0.85;
    const rest15 = betriebsvermoegen * 0.15;
    // Gleitender Abzugsbetrag nach § 13a Abs. 2 ErbStG (max 150.000 €, schmilzt zwischen 150.000 € und 300.000 € ab)
    let abzugsbetrag = 0;
    if (rest15 <= 150000) {
      abzugsbetrag = rest15;
    } else if (rest15 < 300000) {
      abzugsbetrag = Math.max(0, 150000 - (rest15 - 150000) * 0.50);
    } else {
      abzugsbetrag = 0;
    }
    steuerfreiesBetriebsvermoegen = Math.min(betriebsvermoegen, Math.round(abschlag85 + abzugsbetrag));
    verbleibendesSteuerpflichtigesBetriebsvermoegen = Math.max(0, betriebsvermoegen - steuerfreiesBetriebsvermoegen);
  } else {
    steuerfreiesBetriebsvermoegen = 0;
    verbleibendesSteuerpflichtigesBetriebsvermoegen = betriebsvermoegen;
  }

  // 6. Zugewinnausgleich (§ 5 ErbStG)
  const zugewinnEhe = (inputs.verwandtschaft === 'ehegatte' || inputs.verwandtschaft === 'lebenspartner')
    ? Math.max(0, inputs.zugewinnAusgleich || 0)
    : 0;
  const steuerfreierZugewinn = Math.min(privatvermoegen, zugewinnEhe);

  const steuerbeguenstigtesVermoegenGesamt = 
    steuerfreierHausrat + steuerfreieImmobilie + steuerfreiesBetriebsvermoegen + steuerfreierZugewinn;

  // 7. Schulden & Erbfallkosten (§ 10 Abs. 5 ErbStG)
  const schuldenAbzug = Math.max(0, inputs.schulden || 0);
  let erbfallkostenAbzug = 15000; // Standard gesetzliche Pauschale ab 2025/2026/2027 (§ 10 Abs. 5 Nr. 3 ErbStG)
  if (inputs.erbfallkostenArt === 'individuell') {
    erbfallkostenAbzug = Math.max(15000, inputs.erbfallkostenBetrag || 0);
  }
  const gesamteNachlassverbindlichkeiten = schuldenAbzug + erbfallkostenAbzug;

  // 8. Reiner steuerlicher Erwerb vor persönlichen Freibeträgen
  const steuerpflichtigeAktiva = Math.max(0, bruttoNachlass - steuerbeguenstigtesVermoegenGesamt);
  const reinerErwerb = Math.max(0, steuerpflichtigeAktiva - gesamteNachlassverbindlichkeiten);

  // 9. Versorgungsfreibetrag (§ 17 ErbStG)
  let maximalerVersorgungsfreibetrag = 0;
  if (inputs.verwandtschaft === 'ehegatte' || inputs.verwandtschaft === 'lebenspartner') {
    maximalerVersorgungsfreibetrag = 256000;
  } else if (inputs.verwandtschaft === 'kind' || inputs.verwandtschaft === 'stiefkind') {
    const alter = inputs.kindAlterJahre ?? 20;
    if (alter <= 5) maximalerVersorgungsfreibetrag = 52000;
    else if (alter <= 10) maximalerVersorgungsfreibetrag = 41000;
    else if (alter <= 15) maximalerVersorgungsfreibetrag = 30700;
    else if (alter <= 20) maximalerVersorgungsfreibetrag = 20500;
    else if (alter <= 27) maximalerVersorgungsfreibetrag = 10300;
    else maximalerVersorgungsfreibetrag = 0;
  }
  const rentenKuerzung = Math.max(0, inputs.rentenVersorgungKapitalwert || 0);
  const versorgungsfreibetrag = Math.max(0, maximalerVersorgungsfreibetrag - rentenKuerzung);

  // 10. Pflegefreibetrag (§ 13 Abs. 1 Nr. 9 ErbStG) bis max 20.000 €
  const pflegefreibetrag = Math.min(20000, Math.max(0, inputs.pflegeleistungenWert || 0));

  // 11. Anrechnung früherer Schenkungen (§ 14 ErbStG)
  const vorerwerbe = Math.max(0, inputs.vorerwerbe10Jahre || 0);
  const persoenlicherFreibetragVerfuegbar = Math.max(0, persoenlicherFreibetrag - vorerwerbe);

  // Gesamte abzugsfähige Freibeträge
  const freibetragGesamt = persoenlicherFreibetragVerfuegbar + versorgungsfreibetrag + pflegefreibetrag;
  const effektivGenutzterFreibetrag = Math.min(reinerErwerb, freibetragGesamt);

  // 12. Steuerpflichtiger Erwerb nach § 10 Abs. 1 Satz 6 ErbStG (auf volle 100 € nach unten abgerundet)
  const steuerpflichtigerErwerbUnabgerundet = Math.max(0, reinerErwerb - freibetragGesamt);
  const steuerpflichtigerErwerb = Math.floor(steuerpflichtigerErwerbUnabgerundet / 100) * 100;

  // 13. Tarif nach § 19 ErbStG
  // Tarifgrenzen: 75.000, 300.000, 600.000, 6.000.000, 13.000.000, 26.000.000 €
  interface TaxTier {
    limit: number;
    rateClass1: number;
    rateClass2: number;
    rateClass3: number;
  }
  const taxTiers: TaxTier[] = [
    { limit: 75000, rateClass1: 7, rateClass2: 15, rateClass3: 30 },
    { limit: 300000, rateClass1: 11, rateClass2: 20, rateClass3: 30 },
    { limit: 600000, rateClass1: 15, rateClass2: 25, rateClass3: 30 },
    { limit: 6000000, rateClass1: 19, rateClass2: 30, rateClass3: 30 },
    { limit: 13000000, rateClass1: 23, rateClass2: 35, rateClass3: 50 },
    { limit: 26000000, rateClass1: 27, rateClass2: 40, rateClass3: 50 },
    { limit: Infinity, rateClass1: 30, rateClass2: 43, rateClass3: 50 }
  ];

  function getTaxRate(amount: number, cls: 'I' | 'II' | 'III'): { rate: number; tierIndex: number } {
    for (let i = 0; i < taxTiers.length; i++) {
      if (amount <= taxTiers[i].limit) {
        const rate = cls === 'I' ? taxTiers[i].rateClass1 : cls === 'II' ? taxTiers[i].rateClass2 : taxTiers[i].rateClass3;
        return { rate, tierIndex: i };
      }
    }
    const last = taxTiers[taxTiers.length - 1];
    return {
      rate: cls === 'I' ? last.rateClass1 : cls === 'II' ? last.rateClass2 : last.rateClass3,
      tierIndex: taxTiers.length - 1
    };
  }

  const { rate: steuersatzProzent, tierIndex } = getTaxRate(steuerpflichtigerErwerb, steuerklasse);
  const tariflicheSteuer = Math.round(steuerpflichtigerErwerb * (steuersatzProzent / 100));

  // 14. Härteausgleich (§ 19 Abs. 3 ErbStG)
  // Greift, wenn der Erwerb knapp über der Stufengrenze liegt
  let haerteausgleichAngewendet = false;
  let haerteausgleichErsparnis = 0;

  if (tierIndex > 0 && steuerpflichtigerErwerb > 0) {
    const prevTier = taxTiers[tierIndex - 1];
    const prevLimit = prevTier.limit;
    const prevRate = steuerklasse === 'I' ? prevTier.rateClass1 : steuerklasse === 'II' ? prevTier.rateClass2 : prevTier.rateClass3;

    if (steuersatzProzent > prevRate) {
      const taxAtPrevLimit = prevLimit * (prevRate / 100);
      const excess = steuerpflichtigerErwerb - prevLimit;
      // Steuer wird auf Steuer der Vorstufe zzgl. 50% des Mehrbetrags gedeckelt (§ 19 Abs. 3 Satz 2)
      const cappedTax = taxAtPrevLimit + (excess * 0.50);
      if (cappedTax < tariflicheSteuer) {
        haerteausgleichAngewendet = true;
        haerteausgleichErsparnis = Math.round(tariflicheSteuer - cappedTax);
      }
    }
  }

  // 15. Tarifentlastung für Betriebsvermögen (§ 19a ErbStG)
  let tarifentlastung19a = 0;
  if ((steuerklasse === 'II' || steuerklasse === 'III') && verbleibendesSteuerpflichtigesBetriebsvermoegen > 0) {
    // Auf das begünstigte Betriebsvermögen wird der günstigere Steuersatz der Steuerklasse I angewendet
    const { rate: class1Rate } = getTaxRate(steuerpflichtigerErwerb, 'I');
    if (steuersatzProzent > class1Rate) {
      const betriebsAnteil = Math.min(1, verbleibendesSteuerpflichtigesBetriebsvermoegen / (steuerpflichtigerErwerb || 1));
      const ersparnisProzent = (steuersatzProzent - class1Rate) / 100;
      tarifentlastung19a = Math.round(steuerpflichtigerErwerb * betriebsAnteil * ersparnisProzent);
    }
  }

  // 16. Endgültige Erbschaftsteuer & Netto-Erbe
  const endgueltigeSteuer = Math.max(0, tariflicheSteuer - haerteausgleichErsparnis - tarifentlastung19a);
  const nettoErbschaft = Math.max(0, bruttoNachlass - gesamteNachlassverbindlichkeiten - endgueltigeSteuer);
  const effektiverSteuersatz = bruttoNachlass > 0 ? (endgueltigeSteuer / bruttoNachlass) * 100 : 0;

  return {
    steuerklasse,
    steuerklasseNum,
    bruttoNachlass,
    steuerfreierHausrat,
    steuerfreieImmobilie,
    steuerfreiesBetriebsvermoegen,
    steuerfreierZugewinn,
    steuerbeguenstigtesVermoegenGesamt,
    schuldenAbzug,
    erbfallkostenAbzug,
    gesamteNachlassverbindlichkeiten,
    reinerErwerb,
    persoenlicherFreibetrag,
    persoenlicherFreibetragVerfuegbar,
    versorgungsfreibetrag,
    pflegefreibetrag,
    freibetragGesamt,
    effektivGenutzterFreibetrag,
    steuerpflichtigerErwerbUnabgerundet,
    steuerpflichtigerErwerb,
    steuersatzProzent,
    tariflicheSteuer,
    haerteausgleichAngewendet,
    haerteausgleichErsparnis,
    tarifentlastung19a,
    erbschaftsteuer: endgueltigeSteuer,
    effektiverSteuersatz,
    nettoErbschaft
  };
}


